import React, { useEffect, useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { CreditCard, Smartphone, Globe, Wallet, Loader2, CheckCircle2, ExternalLink, AlertCircle } from 'lucide-react';
import { tenantAPI } from '../../lib/api';
import { toast } from 'sonner';

const ICONS = {
  mobile_money: Smartphone,
  card: CreditCard,
  aggregator: Globe,
  other: Wallet,
};

const fmt = (v) => new Intl.NumberFormat('fr-FR').format(Math.max(0, Number(v) || 0));

export default function TenantPayNow({ open, onClose, dueNotices = [], initialNoticeId = null, onPaid }) {
  const [loading, setLoading] = useState(false);
  const [methods, setMethods] = useState([]);
  const [noticeId, setNoticeId] = useState(initialNoticeId || '');
  const [amount, setAmount] = useState('');
  const [provider, setProvider] = useState('');
  const [phone, setPhone] = useState('');
  const [extTxId, setExtTxId] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [polling, setPolling] = useState(false);
  const [resultTx, setResultTx] = useState(null);

  const unpaidNotices = useMemo(
    () => (dueNotices || []).filter((n) => n.status === 'en_attente' || n.status === 'en_retard'),
    [dueNotices],
  );

  const selectedNotice = useMemo(
    () => unpaidNotices.find((n) => n.id === noticeId) || null,
    [noticeId, unpaidNotices],
  );

  const selectedMethod = useMemo(
    () => methods.find((m) => m.provider === provider) || null,
    [provider, methods],
  );

  useEffect(() => {
    if (!open) return;
    setResultTx(null);
    setExtTxId('');
    setNote('');
    (async () => {
      setLoading(true);
      try {
        const { data } = await tenantAPI.getAvailablePaymentMethods();
        setMethods(data.items || []);
      } catch (e) {
        toast.error("Impossible de charger les méthodes de paiement");
        setMethods([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  useEffect(() => {
    if (open) {
      const nid = initialNoticeId || unpaidNotices[0]?.id || '';
      setNoticeId(nid);
    }
  }, [open, initialNoticeId, unpaidNotices]);

  useEffect(() => {
    if (selectedNotice) {
      setAmount(String(Math.round(selectedNotice.amount || 0)));
    }
  }, [selectedNotice]);

  const canSubmit = () => {
    if (!noticeId) return false;
    const n = Number(amount);
    if (!n || n <= 0) return false;
    if (!provider) return false;
    if (selectedMethod?.flow === 'manual') {
      if (!phone || phone.replace(/\D/g, '').length < 8) return false;
      if (!extTxId || extTxId.trim().length < 3) return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!canSubmit()) {
      toast.error('Veuillez compléter tous les champs');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        notice_id: noticeId,
        amount: Number(amount),
        provider,
        return_origin: window.location.origin,
        phone_number: phone || undefined,
        external_transaction_id: extTxId || undefined,
        note: note || undefined,
      };
      const { data } = await tenantAPI.initiatePayment(payload);
      setResultTx(data);
      if (data.checkout_url) {
        // Ouvre un onglet avec la page de paiement
        window.open(data.checkout_url, '_blank', 'noopener');
        // Poll le statut toutes les 4s pendant 2 minutes
        setPolling(true);
        pollStatus(data.tx_id, 30);
      } else {
        toast.success(data.message || 'Paiement enregistré en attente de confirmation');
        if (onPaid) onPaid(data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur à l\'initiation du paiement');
    } finally {
      setSubmitting(false);
    }
  };

  const pollStatus = async (txId, attempts) => {
    if (attempts <= 0) {
      setPolling(false);
      return;
    }
    try {
      const { data } = await tenantAPI.getPaymentStatus(txId);
      if (data?.status === 'confirme' || data?.status === 'paye') {
        setResultTx((r) => ({ ...(r || {}), ...data, status: 'confirme' }));
        setPolling(false);
        toast.success('Paiement confirmé');
        if (onPaid) onPaid(data);
        return;
      }
      if (data?.status === 'echec') {
        setPolling(false);
        toast.error('Paiement refusé');
        return;
      }
    } catch (_) {}
    setTimeout(() => pollStatus(txId, attempts - 1), 4000);
  };

  const close = () => {
    if (submitting || polling) return;
    onClose && onClose();
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-xl" data-testid="tenant-pay-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1B3A4B]">
            <Wallet className="w-5 h-5 text-[#B85C38]" />
            Payer mon loyer
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Chargement…
          </div>
        ) : resultTx ? (
          <div className="py-4 space-y-4" data-testid="tenant-pay-result">
            {resultTx.status === 'confirme' || resultTx.status === 'paye' ? (
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center mx-auto mb-3">
                  <CheckCircle2 className="w-10 h-10 text-white" />
                </div>
                <h3 className="font-bold text-lg">Paiement confirmé</h3>
                <p className="text-sm text-slate-600">Un reçu sera disponible dans Quittances.</p>
              </div>
            ) : resultTx.checkout_url ? (
              <div className="text-center space-y-3">
                <div className="w-16 h-16 rounded-full bg-blue-500 flex items-center justify-center mx-auto">
                  <Loader2 className="w-10 h-10 text-white animate-spin" />
                </div>
                <h3 className="font-bold">En attente de paiement</h3>
                <p className="text-sm text-slate-600">
                  Terminez le paiement dans l'onglet qui vient de s'ouvrir. Nous vérifions le statut automatiquement.
                </p>
                <Button
                  variant="outline"
                  onClick={() => window.open(resultTx.checkout_url, '_blank', 'noopener')}
                  data-testid="reopen-checkout-btn"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Rouvrir la page de paiement
                </Button>
              </div>
            ) : (
              <div className="text-center space-y-2">
                <AlertCircle className="w-12 h-12 text-amber-500 mx-auto" />
                <h3 className="font-bold">Paiement en attente de confirmation</h3>
                <p className="text-sm text-slate-600">
                  {resultTx.message || 'Votre déclaration a été enregistrée. L\'agence confirmera après vérification.'}
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Avis d'échéance */}
            <div>
              <Label className="text-xs">Échéance à payer</Label>
              <Select value={noticeId} onValueChange={setNoticeId}>
                <SelectTrigger data-testid="pay-notice-select">
                  <SelectValue placeholder="Sélectionner un mois" />
                </SelectTrigger>
                <SelectContent>
                  {unpaidNotices.length === 0 ? (
                    <SelectItem value="none" disabled>Aucune échéance impayée</SelectItem>
                  ) : (
                    unpaidNotices.map((n) => (
                      <SelectItem key={n.id} value={n.id}>
                        {n.period_month || n.period_start?.slice(0, 7)} · {fmt(n.amount)} XOF
                        {n.status === 'en_retard' ? ' (en retard)' : ''}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Montant (paiement partiel autorisé) */}
            <div>
              <Label className="text-xs">
                Montant à payer (XOF)
                {selectedNotice && (
                  <span className="text-slate-500 ml-2">
                    · Total: {fmt(selectedNotice.amount)} XOF
                  </span>
                )}
              </Label>
              <Input
                type="number"
                min="1"
                step="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Vous pouvez payer en plusieurs fois"
                data-testid="pay-amount-input"
              />
              {selectedNotice && Number(amount) > 0 && Number(amount) < Number(selectedNotice.amount) && (
                <p className="text-xs text-amber-600 mt-1">
                  Paiement partiel · Reste à payer : {fmt(Number(selectedNotice.amount) - Number(amount))} XOF
                </p>
              )}
            </div>

            {/* Méthodes de paiement */}
            <div>
              <Label className="text-xs">Moyen de paiement</Label>
              {methods.length === 0 ? (
                <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
                  Aucun moyen de paiement n'est actuellement activé par votre agence. Contactez-la pour régler votre loyer.
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 mt-2" data-testid="pay-methods-list">
                  {methods.map((m) => {
                    const Icon = ICONS[m.category] || Wallet;
                    const active = provider === m.provider;
                    return (
                      <button
                        key={m.provider}
                        type="button"
                        onClick={() => setProvider(m.provider)}
                        className={`p-3 rounded-xl border-2 text-left transition-all ${
                          active ? 'border-[#B85C38] bg-[#FBF6EE]' : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`pay-method-${m.provider}`}
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center text-white"
                            style={{ background: m.color || '#64748B' }}
                          >
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold text-sm truncate">{m.label}</div>
                            {m.flow === 'manual' && m.info?.merchant_number && (
                              <div className="text-xs text-slate-500 truncate">{m.info.merchant_number}</div>
                            )}
                            {m.flow !== 'manual' && (
                              <div className="text-xs text-emerald-600">Paiement instantané</div>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Formulaire additionnel pour MOBILE MONEY manuel */}
            {selectedMethod?.flow === 'manual' && (
              <div className="space-y-3 p-3 rounded-xl bg-slate-50 border border-slate-200">
                <div className="text-xs text-slate-600 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-500" />
                  <div>
                    <b>Comment faire :</b> envoyez le montant via votre app {selectedMethod.label} vers le numéro
                    <b className="mx-1">{selectedMethod.info?.merchant_number || '—'}</b>
                    ({selectedMethod.info?.merchant_name || 'Agence'}), puis collez l'ID de la transaction reçue ci-dessous.
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Votre numéro de téléphone</Label>
                  <Input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+223 76 00 00 00"
                    data-testid="pay-phone-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">ID de transaction (reçu après le paiement)</Label>
                  <Input
                    value={extTxId}
                    onChange={(e) => setExtTxId(e.target.value)}
                    placeholder="Ex: MP12345678"
                    data-testid="pay-ext-tx-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Note (optionnel)</Label>
                  <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={2}
                    placeholder="Précisions à l'attention de l'agence"
                    data-testid="pay-note-input"
                  />
                </div>
              </div>
            )}

            {selectedMethod && selectedMethod.flow !== 'manual' && (
              <Badge variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-700">
                Vous allez être redirigé vers {selectedMethod.label}
              </Badge>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={submitting || polling} data-testid="pay-cancel-btn">
            Fermer
          </Button>
          {!resultTx && (
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit() || submitting}
              className="bg-[#B85C38] hover:bg-[#a04d2f] text-white"
              data-testid="pay-submit-btn"
            >
              {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Traitement…</>) : (
                <>Payer {amount ? `${fmt(amount)} XOF` : ''}</>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
